import React, { useEffect, useState } from "react";
import {
  addRoom,
  changeRoomStatus,
  getRoomData,
  updateRoom,
} from "../utils/helperFunctions";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { MdEdit, MdContentCopy, MdAdd } from "react-icons/md";

const RoomMaster = ({ role }) => {
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [Room, setRoom] = useState({
    roomNo: "",
    floorNo: "",
    roomName: "",
    gracePeriod: "",
    hourlyPricing: [{ hours: "", price: "" }],
  });
  const [roomData, setRoomData] = useState([]);
  const [errors, setErrors] = useState({
    roomNo: "",
    floorNo: "",
    roomName: "",
    gracePeriod: "",
    nightStayPrice: "",
    pricing: [],
  });

  useEffect(() => {
    fetchRoomsdata();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showModal) {
        handleCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal]);

  const fetchRoomsdata = async () => {
    setIsLoading(true);
    try {
      const response = await getRoomData();
      setRoomData(response.data);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddmodel = () => {
    setIsEditing(false);
    setShowModal(true);
  };

  const handleDuplicate = (room) => {
    setIsEditing(false);
    setShowModal(true);
    setRoom({
      ...room,
      _id: undefined,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoom((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      roomNo: "",
      roomName: "",
      gracePeriod: "",
      pricing: [],
    };

    if (!Room.roomNo.trim()) {
      newErrors.roomNo = "Room No is required";
      isValid = false;
    } else if (
      roomData.some(
        (r) => r.roomNo === Room.roomNo && (!isEditing || r._id !== Room._id)
      )
    ) {
      newErrors.roomNo = "Room No already exists";
      isValid = false;
    }

    if (!Room.roomName.trim()) {
      newErrors.roomName = "Room Name is required";
      isValid = false;
    }
    if (!Room.floorNo.trim()) {
      newErrors.floorNo = "FloorNo is required";
      isValid = false;
    }

    if (
      Room.gracePeriod === "" ||
      Room.gracePeriod === null ||
      Room.gracePeriod === undefined
    ) {
      newErrors.gracePeriod = "Grace Period is required";
      isValid = false;
    } else if (isNaN(Room.gracePeriod)) {
      newErrors.gracePeriod = "Grace Period must be a number";
      isValid = false;
    } else if (Room.gracePeriod < 0) {
      newErrors.gracePeriod = "Grace Period cannot be negative";
      isValid = false;
    }
    if (
      Room.nightStayPrice === "" ||
      Room.nightStayPrice === null ||
      Room.nightStayPrice === undefined
    ) {
      newErrors.nightStayPrice = "Night Stay Price is required";
      isValid = false;
    } else if (isNaN(Room.nightStayPrice)) {
      newErrors.nightStayPrice = "Night Stay Price must be a number";
      isValid = false;
    } else if (Room.nightStayPrice < 0) {
      newErrors.nightStayPrice = "Night Stay Price cannot be negative";
      isValid = false;
    }

    const hoursSet = new Set();
    Room.hourlyPricing.forEach((pricing, index) => {
      const priceError = { hours: "", price: "" };
      const hours = Number(pricing.hours);

      if (!pricing.hours && pricing.hours !== 0) {
        priceError.hours = "Hours is required";
        isValid = false;
      } else if (isNaN(hours)) {
        priceError.hours = "Hours must be a number";
        isValid = false;
      } else if (hours <= 0) {
        priceError.hours = "Hours must be greater than 0";
        isValid = false;
      } else if (hoursSet.has(hours)) {
        priceError.hours = "Duplicate hours value";
        isValid = false;
      } else {
        hoursSet.add(hours);
      }

      const price = Number(pricing.price);
      if (!pricing.price && pricing.price !== 0) {
        priceError.price = "Price is required";
        isValid = false;
      } else if (isNaN(price)) {
        priceError.price = "Price must be a number";
        isValid = false;
      } else if (price <= 0) {
        priceError.price = "Price must be greater than 0";
        isValid = false;
      }

      newErrors.pricing[index] = priceError;
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSaveRoom = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let response;
      if (!isEditing) {
        response = await addRoom(Room);
        toast.success(response.data.msg);
      } else {
        response = await updateRoom(Room);
        toast.success(response.data.msg);
      }
      if (response.data.status) {
        await fetchRoomsdata();
        handleCancel();
      }
    } catch (error) {
      console.error(error);
      // toast.error(error.response?.data?.msg || "Failed to save room");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (room) => {
    setIsEditing(true);
    setShowModal(true);
    setRoom(room);
  };

  const toggleRoomStatus = async (room, newStatus) => {
    try {
      const response = await changeRoomStatus(room._id, newStatus);
      setIsLoading(true);
      if (response.data.status) {
        toast.success(
          `Room ${newStatus ? "activated" : "deactivated"} successfully`
        );
        fetchRoomsdata();
      }
    } catch (error) {
      console.error("Error updating room status:", error);
      toast.error(error.response?.data?.msg || "Failed to update room status");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePricingChange = (index, field, value) => {
    const updatedPricing = [...Room.hourlyPricing];
    updatedPricing[index][field] = value;
    setRoom((prev) => ({ ...prev, hourlyPricing: updatedPricing }));

    if (errors.pricing[index]?.[field]) {
      const updatedErrors = [...errors.pricing];
      updatedErrors[index] = { ...updatedErrors[index], [field]: "" };
      setErrors((prev) => ({ ...prev, pricing: updatedErrors }));
    }
  };

  const addPricingOption = () => {
    setRoom((prev) => ({
      ...prev,
      hourlyPricing: [...prev.hourlyPricing, { hours: "", price: "" }],
    }));
  };

  const removePricingOption = (index) => {
    setRoom((prev) => ({
      ...prev,
      hourlyPricing: prev.hourlyPricing.filter((_, i) => i !== index),
    }));
  };

  const handleCancel = () => {
    setShowModal(false);
    setRoom({
      roomNo: "",
      roomName: "",
      gracePeriod: "",
      hourlyPricing: [{ hours: "", price: "" }],
    });
    setIsEditing(false);
    setErrors({
      roomNo: "",
      roomName: "",
      gracePeriod: "",
      pricing: [],
    });
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 ">
      <div className="container mx-auto mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Room Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage all room configurations and pricing
              </p>
            </div>

            {role !== "view only" && (
              <div className="flex items-center">
                <button
                  onClick={openAddmodel}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <MdAdd className="text-lg" />
                  Add Room
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Total Rooms
                  </p>
                  <p className="text-2xl font-bold mt-1">{roomData.length}</p>
                </div>
                <div className="bg-indigo-100 p-3 rounded-full">
                  <svg
                    className="h-6 w-6 text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Active Rooms
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {roomData.filter((r) => r.roomStatus).length}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <svg
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Inactive Rooms
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {roomData.filter((r) => !r.roomStatus).length}
                  </p>
                </div>
                <div className="bg-amber-100 p-3 rounded-full">
                  <svg
                    className="h-6 w-6 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className=" mx-auto bg-white shadow-sm overflow-hidden">
          <div className="p-4 md:p-6">
            {isLoading && roomData.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : roomData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <p className="mt-4 text-lg font-medium text-gray-500">
                  No rooms found
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Room No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Floor No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Room Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Grace Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Night Stay Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Pricing
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Status
                      </th>
                      {role !==
                        "view only" && (
                          <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                            Actions
                          </th>
                        )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {roomData.map((room, index) => (
                      <motion.tr
                        key={room._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`transition-colors duration-150 ${
                          index % 2 === 0 ? "bg-white" : "bg-indigo-50/30"
                        } hover:bg-indigo-100/30`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {room.roomNo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {room.floorNo} Floor
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {room.roomName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {room.gracePeriod} min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          ₹{room.nightStayPrice}
                        </td>

                        <td className="px-2 sm:px-4 py-3 sm:py-4">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                            {room.hourlyPricing.map((pricing, i) => (
                              <div
                                key={i}
                                className="text-xs bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full text-center font-medium break-words min-w-0"
                              >
                                {pricing.hours}h: ₹{pricing.price}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() =>
                              toggleRoomStatus(room, !room.roomStatus)
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              room.roomStatus ? "bg-indigo-600" : "bg-gray-200"
                            }`}
                            disabled={isLoading || role === "view only"}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                room.roomStatus
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </td>

                        {role !== "view only" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(room)}
                                className="text-indigo-600 hover:text-indigo-900 p-1"
                                title="Edit"
                              >
                                <MdEdit className="h-6 w-6" />
                              </button>
                              <button
                                onClick={() => handleDuplicate(room)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Duplicate"
                              >
                                <MdContentCopy className="h-6 w-6" />
                              </button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {showModal && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            style={{
              overflow: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
              >
                <div
                  className="absolute inset-0 bg-gray-500 opacity-75"
                  onClick={handleCancel}
                ></div>
              </div>
              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-lg w-full mx-2 sm:mx-0">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {isEditing ? "Edit Room" : "Add New Room"}
                  </h3>
                  <form onSubmit={handleSaveRoom} className="space-y-4">
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 rounded-lg">
                      <div className="space-y-3 sm:space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Room No *
                          </label>
                          <input
                            type="text"
                            name="roomNo"
                            value={Room.roomNo}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                            required
                          />
                          {errors.roomNo && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.roomNo}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Floor No *
                          </label>
                          <input
                            type="text"
                            name="floorNo"
                            value={Room.floorNo}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            required
                          />
                          {errors.floorNo && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.floorNo}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Room Name *
                          </label>
                          <input
                            type="text"
                            name="roomName"
                            value={Room.roomName}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                            required
                          />
                          {errors.roomName && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.roomName}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Grace Period (minutes) *
                          </label>
                          <input
                            type="number"
                            name="gracePeriod"
                            value={Room.gracePeriod}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                            required
                            onWheel={(e) => e.target.blur()}
                          />
                          {errors.gracePeriod && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.gracePeriod}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Night Stay Price *
                          </label>
                          <input
                            type="number"
                            name="nightStayPrice"
                            value={Room.nightStayPrice}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                            required
                            min="0"
                            onWheel={(e) => e.target.blur()}
                          />
                          {errors.nightStayPrice && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.nightStayPrice}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-2 sm:p-4 rounded-lg">
                      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-700">
                        Hourly Pricing
                      </h3>

                      <div className="space-y-3 sm:space-y-4">
                        {Room.hourlyPricing?.map((p, i) => (
                          <div
                            key={i}
                            className="border border-gray-300 rounded-lg p-3 sm:p-4"
                          >
                            <div className="grid sm:grid-cols-12 grid-cols-6 gap-3 sm:gap-4 items-end">
                              <div className="col-span-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Hours *
                                </label>
                                <input
                                  type="number"
                                  value={p.hours}
                                  onChange={(e) =>
                                    handlePricingChange(
                                      i,
                                      "hours",
                                      e.target.value
                                    )
                                  }
                                  className={`w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                                  min="0"
                                  onWheel={(e) => e.target.blur()}
                                  required
                                />
                                {errors.pricing?.[i]?.hours && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.pricing[i].hours}
                                  </p>
                                )}
                              </div>

                              <div className="col-span-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Price *
                                </label>
                                <input
                                  type="number"
                                  value={p.price}
                                  onChange={(e) =>
                                    handlePricingChange(
                                      i,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  className={`w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                                  min="0"
                                  onWheel={(e) => e.target.blur()}
                                  required
                                />
                                {errors.pricing?.[i]?.price && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.pricing[i].price}
                                  </p>
                                )}
                              </div>

                              {i !== 0 && (
                                <div className="col-span-2 flex sm:justify-end ">
                                  <button
                                    type="button"
                                    onClick={() => removePricingOption(i)}
                                    className="text-red-500 hover:text-red-700 transition p-2 rounded-full hover:bg-red-50"
                                    title="Remove pricing option"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-6 w-6"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={addPricingOption}
                          className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 outline-none"
                        >
                          + Add Pricing Option
                        </button>
                      </div>
                    </div>

                    <div className="mt-6 flex space-x-2">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 ${
                          isLoading ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <svg
                              className="mr-2 h-4 w-4 animate-spin"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {isEditing ? "Updating..." : "Adding..."}
                          </>
                        ) : isEditing ? (
                          "Update Room"
                        ) : (
                          "Add Room"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 "
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomMaster;
